/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// redux
import {useDispatch, useSelector} from 'react-redux';
import { fetchAccountCommentingRoles, PRODUCT_COMMENTING } from '../../../../../state/thunks/account';
import { toggleAccountRoleForm, closeAccountRoleForms } from '../../../../../state/slices/account';
import { createCustomCommentingRole, updateCustomCommentingRole  } from '../../../../../state/thunks/commenting';
// utils
// material
import { ListTable, Group, Button, Title, Link } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
// content
// styles
import injectSheet from 'react-jss';
import styles from './rolesmanagement_styles';
import RoleForm from './roleform/roleform';

const defaultCols = { roleName: 'Role Name', badgeImage: 'Avatar', action: 'Action'}

/* ========== ~~~~~~~~~~ Users Config ~~~~~~~~~~ ========== */
const RolesManagement = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { accountCommentingRoles, accountProducts, addingCommentingRoleStatus, roleFormsOpened, updatingCommentingRoles, } = useSelector(state => state.account);
  const { accountUUID, userUUID: sessionUserUUID, permissions: sessionPermissions } = useSelector(state => state.session);
  const hasCommenting = accountProducts?.includes(PRODUCT_COMMENTING)

  const [isOpened, setIsOpened] = useState(false);
  const [isNewRoleFormShown, showNewRoleForm] = useState(false);

  useEffect(() => {
    return () => {
      // close all forms when  go to other page
      // dispatch(closeAccountUserForms())
      showNewRoleForm(false)
    }
  },[])

  useEffect(() => {
    dispatch(fetchAccountCommentingRoles(accountUUID))
  }, [accountUUID])

  const hasChangeUserPermissions = useMemo(() => sessionPermissions?.includes('settings.users') || false, [sessionPermissions]);

  const handleToggle = () => {
    setIsOpened(!isOpened)
    // dispatch(closeAccountUserForms())
    showNewRoleForm(false)
  }

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleToggle}
      isOpened={isOpened}
      configTitle={'View'}
      cancelTitle={'Close'}
      title='Roles Management'
    />
  );

  const formattedRoleData = () => accountCommentingRoles.map(role => {
    const { roleName, badgeImageURL, uuid: roleUUID } = role;
    const showEdit = roleFormsOpened.length > 0 ? roleFormsOpened.includes(roleUUID) : true;
    return {
      roleName,
      badgeImage: <img className={'badgeimg'} src={badgeImageURL} />,
      action: showEdit && (
        <div className={'actioncolumn'}>
          <Link icon={'edit'} disabled={false} handleClick={() => toggleRoleForm(roleUUID)}/>
        </div>
      ),
      expand: roleFormsOpened.includes(roleUUID),
      expandable: roleFormsOpened.includes(roleUUID) && (
        <RoleForm
          title={`Edit Role - ${roleName}`} 
          data={{...role, roleUUID, accountUUID}} 
          isProcessing={updatingCommentingRoles.includes(roleUUID)} 
          onSave={handleUpdateRole} 
          onCancel={() => toggleRoleForm(roleUUID)}
        />
      )
    }
  })

  const toggleRoleForm = roleUUID => dispatch(toggleAccountRoleForm(roleUUID))

  const handleAddRole = useCallback(async (requestData) => {
    const response = await dispatch(createCustomCommentingRole(requestData))
    if(!response.error) showNewRoleForm(false)
  }, []);

  const handleUpdateRole = useCallback(async (requestData) => {
    const response = await dispatch(updateCustomCommentingRole(requestData))
    if(!response.error) toggleRoleForm(requestData.roleUUID)
  }, []);

  return (
    <ExpandableSection
      className={`rolesmanagement ${props.classes.rolesmanagement}`}
      controller={controller}
      isOpened={isOpened}
      handleCancel={handleToggle}
      cancelTitle={'Close'}
    >
      {isOpened && <>
        <SectionCard>
          <Group name={'roles-header'}>
            <Title><h2>{ hasChangeUserPermissions ? `Roles` : `Permission Denied`}</h2></Title>
            { !isNewRoleFormShown && hasChangeUserPermissions && <Button text={'New Role'} variant={'primary'} handleClick={() => showNewRoleForm(true)} /> }
          </Group>
          { isNewRoleFormShown && 
            <RoleForm
              data={{accountUUID}}
              title={'New Role'} 
              isProcessing={addingCommentingRoleStatus === 'pending'} 
              onSave={requestData => handleAddRole(requestData)} 
              onCancel={() => showNewRoleForm(false)} 
            />
          } 
          { hasChangeUserPermissions ? 
            <ListTable
              name={`roles`}
              size={`full`}
              columnHeads={defaultCols}
              data={formattedRoleData()}
              rowsPerPage={5}
              paginationLocation={`bottom`}
            />
            :
            <Group name={'permission-denied'}>
              <Title>
                <h4>You don't have permission or access to manage roles</h4>
              </Title>
            </Group>
          }
        </SectionCard>
      </>}
    </ExpandableSection>
  )
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default RolesManagement